/**
 * Práce s obdobími. Presety a porovnávací období pro DateRangePicker.
 * Formát v URL i v API je ISO `YYYY-MM-DD` — deep-link musí přežít refresh.
 */

export type RangePreset = 'today' | 'yesterday' | '7d' | '30d' | '90d' | 'mtd' | 'custom';
export type CompareMode = 'none' | 'prev_period' | 'prev_week';

export interface DateRange {
  from: string;
  to: string;
}

export function toIso(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function fromIso(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

export function addDays(iso: string, days: number): string {
  const date = fromIso(iso);
  date.setDate(date.getDate() + days);
  return toIso(date);
}

/** Počet dní v období včetně obou krajů. */
export function rangeLength(range: DateRange): number {
  const from = fromIso(range.from).getTime();
  const to = fromIso(range.to).getTime();
  return Math.round((to - from) / 86_400_000) + 1;
}

export const PRESET_LABELS: Record<RangePreset, string> = {
  today: 'Dnes',
  yesterday: 'Včera',
  '7d': 'Posledních 7 dní',
  '30d': 'Posledních 30 dní',
  '90d': 'Posledních 90 dní',
  mtd: 'Tento měsíc',
  custom: 'Vlastní rozsah',
};

export function presetRange(preset: RangePreset, today = new Date()): DateRange {
  const t = toIso(today);
  switch (preset) {
    case 'today':
      return { from: t, to: t };
    case 'yesterday': {
      const y = addDays(t, -1);
      return { from: y, to: y };
    }
    case '7d':
      return { from: addDays(t, -6), to: t };
    case '30d':
      return { from: addDays(t, -29), to: t };
    case '90d':
      return { from: addDays(t, -89), to: t };
    case 'mtd': {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: toIso(first), to: t };
    }
    case 'custom':
    default:
      return { from: addDays(t, -6), to: t };
  }
}

/** Odpovídá zvolený rozsah některému presetu? (pro zvýraznění v UI) */
export function detectPreset(range: DateRange, today = new Date()): RangePreset {
  const presets: RangePreset[] = ['today', 'yesterday', '7d', '30d', '90d', 'mtd'];
  for (const preset of presets) {
    const candidate = presetRange(preset, today);
    if (candidate.from === range.from && candidate.to === range.to) return preset;
  }
  return 'custom';
}

/**
 * Porovnávací období.
 * `prev_period` — stejně dlouhé okno těsně před (zrcadlí previous_period() z query.py)
 * `prev_week`   — totéž okno posunuté o 7 dní zpět (drží den v týdnu)
 */
export function compareRange(range: DateRange, mode: CompareMode): DateRange | null {
  if (mode === 'none') return null;
  if (mode === 'prev_week') {
    return { from: addDays(range.from, -7), to: addDays(range.to, -7) };
  }
  const span = rangeLength(range);
  return { from: addDays(range.from, -span), to: addDays(range.from, -1) };
}

export const COMPARE_LABELS: Record<CompareMode, string> = {
  none: 'Bez porovnání',
  prev_period: 'Předchozí období',
  prev_week: 'Předchozí týden',
};

/** „1. 7. – 7. 7. 2026" do hlaviček. */
export function formatRange(range: DateRange): string {
  const fmt = new Intl.DateTimeFormat('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' });
  if (range.from === range.to) return fmt.format(fromIso(range.from));
  return `${fmt.format(fromIso(range.from))} – ${fmt.format(fromIso(range.to))}`;
}

export const WEEKDAY_LABELS = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'] as const;
